/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type NavigateWithFallbackProps = {
  to: string;
  /** Retry delay (default: 400ms) */
  retryMs?: number;
  /** Replace history entry (default: true) */
  replace?: boolean;
  /** If still stuck after retry, do a hard redirect (default: true) */
  hardRedirectAfterRetry?: boolean;
};

/** There was a bug that doing:
 *
 *  <Navigate to="/" replace />
 *
 * wasn't working properly in some cases, leaving users stuck on a blank page.
 * This component attempts to work around that by retrying the navigation after
 * a delay, and if that still doesn't work, doing a hard redirect.
 * It's meant a replacement for <Navigate>
 *
 */
export function NavigateWithFallback({
  to,
  retryMs = 400,
  replace = true,
  hardRedirectAfterRetry = true,
}: NavigateWithFallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [attempt, setAttempt] = useState(0);
  const didNavigateRef = useRef(false);

  const doNavigate = (reason: string) => {
    if (didNavigateRef.current) return;
    didNavigateRef.current = true;

    // Helpful breadcrumbs when debugging
    console.log('[SmartFhirRedirect] navigating', {
      reason,
      from: location.pathname + location.search + location.hash,
      to,
    });

    navigate(to, { replace });
  };

  useEffect(() => {
    // Attempt immediately on mount
    doNavigate('initial');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If we’re still on this route after retryMs, retry once.
    // (In normal cases, the component will unmount and this never runs.)
    const t = window.setTimeout(() => {
      setAttempt((a) => a + 1);

      // Allow a second attempt
      didNavigateRef.current = false;
      doNavigate('retry');

      if (hardRedirectAfterRetry) {
        // If router navigation is being swallowed for some reason, this forces it.
        window.setTimeout(() => {
          if (window.location.pathname.includes('smart-fhir')) {
            console.warn('[SmartFhirRedirect] still on smart-fhir; hard redirecting');
            window.location.assign(to);
          }
        }, 250);
      }
    }, retryMs);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryMs, hardRedirectAfterRetry]);

  return (
    <main style={{ padding: '1rem', color: 'var(--black)' }}>
      <h2 style={{ marginTop: 0 }}>Finishing sign-in…</h2>
      <p>We’re redirecting you to the app.</p>
      <p style={{ opacity: 0.8, marginTop: '1rem' }}>
        If this doesn’t move you automatically, click <a href={to}>here</a>.
      </p>
      {attempt > 0 && <p style={{ opacity: 0.6, marginTop: '1rem', fontSize: 12 }}>Attempt: {attempt + 1}</p>}
    </main>
  );
}
